import React, { useContext } from "react";
import { observer } from "mobx-react-lite";
import { Formik, Form, Field } from "formik";
import Input from '../../../../../FormComponents/Input'
import ProjectAssetPhaseSelect from '../../../../../FormComponents/ProjectAssetPhaseSelect'
import Modal from "../../../../../Modal/Modal";
import DatePicker from 'react-datepicker'
import CampaignScenariosProjectAsset from '../../../../../../models/CampaignScenariosProjectAsset';
import ProjectAsset from '../../../../../../models/ProjectAsset';
import axiosService from "../../../../../../utils/AxiosService";
import { useParams } from "react-router-dom";
import { CheckLg } from "react-bootstrap-icons";
import { toastError } from "../../../../../FormComponents/Toasts";
import { CampaignContext } from "../../../../../../contexts/CampaignContext";
import { TimelineContext } from "../../../../../../contexts/TimelineContext";

const parseDate = (value) => {
  if (!value) return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export default observer(function AddProjectAssetModal({ modalOpen, setModalOpen }) {
  const { campaign, projectAssets, campaignScenarios, primaryScenarioId } = useContext(CampaignContext)
  const { timelineState } = useContext(TimelineContext)
  const { id } = useParams()
  const campaignScenario = campaignScenarios[0]
  const minStartDate = parseDate(campaign.start_date) || undefined
  const maxEndDate = parseDate(campaign.end_date) || undefined
  const themeColors = [
    '#E54747',
    '#E59D47',
    '#E9E12F',
    '#A0E547',
    '#47E590',
    '#47E5E5',
    '#6747E5',
    '#BC47E5',
    '#E547A6'
  ]

  const handleCreate = (project_asset) => {
    axiosService.post(`/api/project_manager/campaigns/${id}/project_assets`, { project_asset, campaign_scenario_id: primaryScenarioId })
      .then(response => {
        const { project_asset, campaign_scenarios_project_asset } = response
        projectAssets.push(new ProjectAsset(project_asset))
       
        if (campaign_scenarios_project_asset) {
          const parsedCspa = typeof campaign_scenarios_project_asset === 'string' 
            ? JSON.parse(campaign_scenarios_project_asset)
            : campaign_scenarios_project_asset

          const cspa = new CampaignScenariosProjectAsset(parsedCspa)
          
          // Update the timeline items with the new instance
          const updatedItems = new Map(timelineState.timelineItems)
          const dateItems = updatedItems.get(cspa.start_date) || []
          
          updatedItems.set(cspa.start_date, [...dateItems, cspa])
          timelineState.timelineItems = updatedItems
          
          // Update the campaign scenario's assets
          if (campaignScenario) {
            campaignScenario.campaign_scenarios_project_assets.push(cspa)
          }
        }
        setModalOpen(false)
      })
      .catch(error => toastError(error))
  }

  return (
    <Modal isOpen={modalOpen} onClose={() => setModalOpen(false) } hasSideBar>
      <Modal.Header>
        <h2 className="dark:text-white">Project Asset</h2>
      </Modal.Header>
      <Modal.Body>
        <Formik
          initialValues={{ ...new ProjectAsset(), phase: 'main' }}
          onSubmit={handleCreate}
        >
          {({handleBlur, handleChange, setFieldValue, values, touched, errors }) => (
            <Form className='w-[370px] -mt-5 dark:bg-rb-dark-mode'>
              <Input
                error={touched.name && errors.name}
                noLabelPadding
                label="Name"
                placeholder="Asset name"
                name="name"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.name}
              />

              <ProjectAssetPhaseSelect
                value={values.phase}
                onChange={handleChange}
                onBlur={handleBlur}
                topMargin="mt-4"
              />

              <div className="flex gap-4 mt-4 mb-4">
                <div className="flex-1">
                  <label className="block mb-2 text-sm font-regular dark:text-white">Start Date</label>
                  <DatePicker
                    selected={values.start}
                    onChange={(date) => setFieldValue('start', date)}
                    className="w-full p-2 border dark:border-rb-dark-mode-lighter dark:bg-rb-dark-mode dark:text-white rounded-lg"
                    dateFormat="dd.MM.yyyy"
                    showPopperArrow={false}
                    popperClassName="react-datepicker-popper"
                    popperPlacement="bottom-start"
                    calendarClassName="react-datepicker"
                    minDate={minStartDate}
                    maxDate={maxEndDate}
                  />
                </div>
                <div className="flex-1">
                  <label className="block mb-2 text-sm font-regular dark:text-white">End Date</label>
                  <DatePicker
                    selected={values.end}
                    onChange={(date) => setFieldValue('end', date)}
                    className="w-full p-2 border dark:border-rb-dark-mode-lighter dark:bg-rb-dark-mode dark:text-white rounded-lg"
                    dateFormat="dd.MM.yyyy"
                    minDate={values.start || minStartDate}
                    maxDate={maxEndDate}
                    showPopperArrow={false}
                    popperClassName="react-datepicker-popper"
                    popperPlacement="bottom-start"
                    calendarClassName="react-datepicker"
                  />
                </div>
              </div>

              <p className="text-sm mt-2 dark:text-white">Asset Color</p>
              <div className="flex px-2 justify-center mt-2">
                {
                  themeColors.map((color, index) => (
                    <label
                      key={index}
                      className="cursor-pointer h-8 w-8 rounded-full border-1 border-white dark:border-rb-dark-mode-lighter -ml-2 flex justify-center items-center"
                      style={{ backgroundColor: color }}
                    >
                      <Field type="radio" name="theme_color" value={color} className="hidden" />
                      {values.theme_color === color ? <CheckLg className='text-white text-xl' /> : ''}
                    </label>
                  ))
                }
              </div>

              <div className="flex justify-between mt-5">
                <button
                  type='button'
                  className='rb-btn-cancel font-bold text-md ml p-2 px-4 rounded-lg'
                  onClick={() => setModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="ml-auto bg-rb-green text-white rounded-lg p-2 px-4 submit text-center active:shadow-outer active:shadow-rb-green border-rb-green dark:text-rb-dark-mode"
                >
                  Create
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  )
})
